export interface ClinicalEnvironmentConfig {
  id: 'bedside_room' | 'exam_room' | 'phlebotomy_chair' | 'icu_bay' | 'handwashing_station' | string;
  name: string;
  category: string;
  setting?: string;
  description: string;
  imageUrl: string;
  image: string;
  photosphereUrl?: string; // 360 degree equirectangular panorama
  defaultHotspotCoordinates: Record<string, { x: number; y: number; label: string; icon: string }>;
  defaultSphericalCoordinates?: Record<string, { yaw: number; pitch: number }>;
}

// Helper: Convert 2D flat coordinates (x: 0-100%, y: 0-100%) to 3D spherical angles (yaw: -180° to +180°, pitch: -85° to +85°)
export const coords2DtoSpherical = (x: number, y: number): { yaw: number; pitch: number } => {
  const yaw = Math.round(((x - 50) / 50) * 85); // maps 0-100% to -85° to +85° in front camera view
  const pitch = Math.round(((50 - y) / 50) * 55); // maps 0-100% to +55° (top) down to -55° (bottom)
  return { yaw, pitch };
};

// Helper: Convert 3D spherical angles to 2D flat coordinates (x: 0-100%, y: 0-100%)
export const sphericalTo2DCoords = (yaw: number, pitch: number): { x: number; y: number } => {
  const x = Math.round(Math.max(5, Math.min(95, 50 + (yaw / 85) * 50)));
  const y = Math.round(Math.max(5, Math.min(95, 50 - (pitch / 55) * 50)));
  return { x, y };
};

export const CLINICAL_ENVIRONMENTS: Record<string, ClinicalEnvironmentConfig> = {
  handwashing_station: {
    id: 'handwashing_station',
    name: 'Clinical Hand Hygiene & Sink Station',
    category: 'Infection Control / Handwashing',
    setting: 'Infection Control / Handwashing',
    description: 'Authentic clinical scrub room featuring stainless steel sink, chrome faucet with running water, wall soap dispenser, paper towel dispenser, and "USED TOWELS" waste bin matching state board testing requirements.',
    imageUrl: '/images/environments/handwashing_station.jpg',
    image: '/images/environments/handwashing_station.jpg',
    photosphereUrl: '/images/environments/handwashing_360.jpg',
    defaultHotspotCoordinates: {
      sink: { x: 44, y: 70, label: 'Hand Hygiene Sink Basin', icon: 'Droplets' },
      bed: { x: 44, y: 52, label: 'Running Water Chrome Faucet', icon: 'Droplets' },
      supplies: { x: 24, y: 28, label: 'Wall Soap Dispenser', icon: 'Box' },
      call_light: { x: 9, y: 44, label: 'Paper Towel Dispenser', icon: 'Box' },
      waste_hamper: { x: 17, y: 84, label: 'Used Towels Waste Bin', icon: 'Trash2' },
      resident: { x: 62, y: 46, label: 'Candidate Lathered Hands', icon: 'User' },
      privacy_curtain: { x: 44, y: 20, label: 'Hand Hygiene Steps Poster', icon: 'Shield' },
    },
    defaultSphericalCoordinates: {
      sink: { yaw: -10, pitch: -22 },
      bed: { yaw: -10, pitch: -2 },
      supplies: { yaw: -42, pitch: 24 },
      call_light: { yaw: -68, pitch: 8 },
      waste_hamper: { yaw: -52, pitch: -38 },
      resident: { yaw: 22, pitch: 5 },
      privacy_curtain: { yaw: -10, pitch: 34 }
    }
  },
  bedside_room: {
    id: 'bedside_room',
    name: 'Long-Term Care Bedside Room',
    category: 'Long-Term Care / CNA',
    setting: 'Long-Term Care / CNA',
    description: 'Authentic resident room with hospital bed, bedside table, emergency call pendant, privacy curtain rail, and hand hygiene sink.',
    imageUrl: '/images/environments/bedside_nurse_patient.jpg',
    image: '/images/environments/bedside_nurse_patient.jpg',
    photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
    defaultHotspotCoordinates: {
      sink: { x: 88, y: 38, label: 'Hand Hygiene Sink', icon: 'Droplets' },
      bed: { x: 42, y: 58, label: 'Hospital Bed & Brakes', icon: 'BedDouble' },
      resident: { x: 38, y: 46, label: 'Resident Patient', icon: 'User' },
      call_light: { x: 24, y: 32, label: 'Emergency Call Light', icon: 'Bell' },
      overbed_table: { x: 64, y: 62, label: 'Overbed Table & Basin', icon: 'Box' },
      supplies: { x: 64, y: 62, label: 'Supply Table & Basin', icon: 'Box' },
      privacy_curtain: { x: 10, y: 35, label: 'Privacy Curtain', icon: 'Shield' },
      waste_hamper: { x: 82, y: 78, label: 'Linen / Waste Hamper', icon: 'Trash2' },
    },
    defaultSphericalCoordinates: {
      sink: { yaw: 64, pitch: 12 },
      bed: { yaw: -14, pitch: -10 },
      resident: { yaw: -20, pitch: 4 },
      call_light: { yaw: -45, pitch: 20 },
      overbed_table: { yaw: 24, pitch: -14 },
      supplies: { yaw: 24, pitch: -14 },
      privacy_curtain: { yaw: -68, pitch: 15 },
      waste_hamper: { yaw: 55, pitch: -32 }
    }
  },
  exam_room: {
    id: 'exam_room',
    name: 'Outpatient Examination Room',
    category: 'Medical Assistant / Clinic',
    setting: 'Medical Assistant / Clinic',
    description: 'Clinical examination room with padded exam table, wall diagnostic station, supply cart, and handwash basin.',
    imageUrl: '/images/environments/exam_room_nurse_patient.jpg',
    image: '/images/environments/exam_room_nurse_patient.jpg',
    photosphereUrl: '/images/environments/exam_room_nurse_patient_360.jpg',
    defaultHotspotCoordinates: {
      bed: { x: 50, y: 55, label: 'Examination Table', icon: 'BedDouble' },
      resident: { x: 50, y: 45, label: 'Exam Patient', icon: 'User' },
      supplies: { x: 78, y: 60, label: 'Diagnostic Cart', icon: 'Box' },
      sink: { x: 18, y: 42, label: 'Sanitizer & Sink', icon: 'Droplets' },
      call_light: { x: 32, y: 30, label: 'Staff Call Button', icon: 'Bell' },
      privacy_curtain: { x: 8, y: 30, label: 'Room Privacy Door', icon: 'Shield' },
      waste_hamper: { x: 85, y: 78, label: 'Biohazard Foot Hamper', icon: 'Trash2' },
    }
  },
  phlebotomy_chair: {
    id: 'phlebotomy_chair',
    name: 'Phlebotomy Blood Draw Station',
    category: 'Phlebotomy / Lab',
    setting: 'Phlebotomy / Lab',
    description: 'Clinical blood draw setup with armrest chair, vacutainer collection tray, sharps container, and sink.',
    imageUrl: '/images/environments/phlebotomy_chair.jpg',
    image: '/images/environments/phlebotomy_chair.jpg',
    photosphereUrl: '/images/environments/phlebotomy_chair_360.jpg',
    defaultHotspotCoordinates: {
      bed: { x: 48, y: 56, label: 'Phlebotomy Chair', icon: 'BedDouble' },
      resident: { x: 48, y: 46, label: 'Donor / Patient Arm', icon: 'User' },
      supplies: { x: 76, y: 58, label: 'Tube Rack & Tourniquet', icon: 'Box' },
      sink: { x: 18, y: 42, label: 'Hand Hygiene Sink', icon: 'Droplets' },
      call_light: { x: 30, y: 28, label: 'Assistance Signal', icon: 'Bell' },
      privacy_curtain: { x: 8, y: 30, label: 'Draw Privacy Screen', icon: 'Shield' },
      waste_hamper: { x: 85, y: 75, label: 'Sharps / Waste Bin', icon: 'Trash2' },
    }
  },
  icu_bay: {
    id: 'icu_bay',
    name: 'High-Acuity ICU Bay',
    category: 'Acute Care / Hospital',
    setting: 'Acute Care / Hospital',
    description: 'Intensive care room with multi-parameter cardiac monitor, electric ICU bed, and oxygen/suction headwall outlets.',
    imageUrl: '/images/environments/icu_bay.jpg',
    image: '/images/environments/icu_bay.jpg',
    defaultHotspotCoordinates: {
      bed: { x: 46, y: 60, label: 'Electric ICU Bed', icon: 'BedDouble' },
      resident: { x: 46, y: 48, label: 'ICU Patient', icon: 'User' },
      call_light: { x: 26, y: 32, label: 'Code Blue / Nurse Call', icon: 'Bell' },
      supplies: { x: 74, y: 58, label: 'IV Infusion Pump & Supplies', icon: 'Box' },
      sink: { x: 88, y: 42, label: 'Surgical Scrub Sink', icon: 'Droplets' },
      privacy_curtain: { x: 12, y: 32, label: 'Isolation Curtain', icon: 'Shield' },
      waste_hamper: { x: 80, y: 78, label: 'Biohazard Container', icon: 'Trash2' },
    }
  }
};

export interface ClinicalSkillFixture {
  id: string;
  label: string;
  category: 'hygiene' | 'resident' | 'furniture' | 'supplies' | 'safety';
  defaultForEnvs: string[];
}

export const CLINICAL_SKILL_FIXTURES: ClinicalSkillFixture[] = [
  { id: 'candidate', label: 'Candidate / Nurse in Royal Blue Scrubs', category: 'hygiene', defaultForEnvs: ['handwashing_station', 'bedside_room', 'exam_room', 'phlebotomy_chair'] },
  { id: 'sink', label: 'Stainless Steel Sink Basin', category: 'hygiene', defaultForEnvs: ['handwashing_station', 'bedside_room'] },
  { id: 'faucet', label: 'Chrome Faucet with Running Water', category: 'hygiene', defaultForEnvs: ['handwashing_station'] },
  { id: 'soap', label: 'Wall-Mounted Liquid Soap Dispenser', category: 'hygiene', defaultForEnvs: ['handwashing_station'] },
  { id: 'towels', label: 'Wall Paper Towel Dispenser', category: 'hygiene', defaultForEnvs: ['handwashing_station'] },
  { id: 'waste_bin', label: 'Stainless Waste Bin ("USED TOWELS")', category: 'hygiene', defaultForEnvs: ['handwashing_station'] },
  { id: 'hygiene_poster', label: 'Hand Hygiene Steps Technique Poster', category: 'hygiene', defaultForEnvs: ['handwashing_station'] },
  { id: 'resident', label: 'Resident / Patient in Scene', category: 'resident', defaultForEnvs: ['bedside_room', 'exam_room', 'phlebotomy_chair', 'icu_bay'] },
  { id: 'bed', label: 'Hospital Bed with Rails & Clean Linens', category: 'furniture', defaultForEnvs: ['bedside_room', 'icu_bay'] },
  { id: 'overbed_table', label: 'Rolling Overbed Table & Basin', category: 'furniture', defaultForEnvs: ['bedside_room'] },
  { id: 'call_light', label: 'Emergency Nurse Call Light Cord', category: 'safety', defaultForEnvs: ['bedside_room'] },
  { id: 'privacy_curtain', label: 'Ceiling Track Privacy Curtain', category: 'safety', defaultForEnvs: ['bedside_room'] },
  { id: 'exam_table', label: 'Padded Exam Table with Paper Roll', category: 'furniture', defaultForEnvs: ['exam_room'] },
  { id: 'phlebotomy_chair', label: 'Phlebotomy Armrest Chair & Tray', category: 'furniture', defaultForEnvs: ['phlebotomy_chair'] },
  { id: 'sharps_bin', label: 'Biohazard Sharps Disposal Container', category: 'safety', defaultForEnvs: ['phlebotomy_chair'] },
  { id: 'wheelchair', label: 'Wheelchair with Locked Brakes & Gait Belt', category: 'furniture', defaultForEnvs: [] },
  { id: 'vitals_cuff', label: 'Manual BP Cuff & Stethoscope', category: 'supplies', defaultForEnvs: [] },
  { id: 'ppe_gear', label: 'PPE Station (Gloves, Isolation Gown & Mask)', category: 'safety', defaultForEnvs: [] },
  { id: 'bath_basin', label: 'Warm Water Wash Basin & Soap Washcloths', category: 'supplies', defaultForEnvs: [] },
  { id: 'clean_linens', label: 'Clean Hospital Linens & Fitted Sheet Stack', category: 'furniture', defaultForEnvs: [] },
  { id: 'meal_tray', label: 'Dining Food Tray, Adaptive Cup & Plate', category: 'supplies', defaultForEnvs: [] },
  { id: 'catheter_kit', label: 'Catheter & Perineal Hygiene Barrier Pads', category: 'supplies', defaultForEnvs: [] },
  { id: 'foot_basin', label: 'Foot Soaking Basin, Lotion & Socks', category: 'supplies', defaultForEnvs: [] },
  { id: 'denture_cup', label: 'Denture Care Cup, Toothbrush & Emesis Basin', category: 'supplies', defaultForEnvs: [] }
];

export function resolveSkillEnvironmentPreset(
  skillTitle?: string,
  preferredEnv?: string
): {
  environment: string;
  imageUrl: string;
  photosphereUrl: string;
  defaultComponentIds: string[];
} {
  const t = (skillTitle || '').toLowerCase().trim();

  // 1. Hand hygiene / infection control / clean hands
  if (t.includes('handwash') || t.includes('hand hygiene') || t.includes('clean hand') || t.includes('asepsis') || preferredEnv === 'handwashing_station') {
    return {
      environment: 'handwashing_station',
      imageUrl: '/images/environments/handwashing_station.jpg',
      photosphereUrl: '/images/environments/handwashing_360.jpg',
      defaultComponentIds: ['candidate', 'sink', 'faucet', 'soap', 'towels', 'waste_bin', 'hygiene_poster']
    };
  }

  // 2. Ambulation / Transfer / Wheelchair / Gait Belt
  if (t.includes('ambulat') || t.includes('gait') || t.includes('transfer') || t.includes('walk') || t.includes('wheelchair')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/ambulation_gait_belt.jpg',
      photosphereUrl: '/images/environments/ambulation_gait_belt_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'wheelchair', 'call_light', 'overbed_table', 'sink']
    };
  }

  // 3. Vital Signs / Radial Pulse / Respiration / Blood Pressure
  if (t.includes('pulse') || t.includes('vital') || t.includes('respirat') || t.includes('blood pressure') || t.includes('bp') || t.includes('heart rate')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/radial_pulse.jpg',
      photosphereUrl: '/images/environments/radial_pulse_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'vitals_cuff', 'overbed_table', 'call_light']
    };
  }

  // 4. Phlebotomy / Blood Draw / Venipuncture
  if (t.includes('phlebotom') || t.includes('venipuncture') || t.includes('blood draw') || t.includes('vacutainer') || preferredEnv === 'phlebotomy_chair') {
    return {
      environment: 'phlebotomy_chair',
      imageUrl: '/images/environments/phlebotomy_chair.jpg',
      photosphereUrl: '/images/environments/phlebotomy_chair_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'phlebotomy_chair', 'sharps_bin', 'sink']
    };
  }

  // 5. PPE / Isolation Precautions
  if (t.includes('ppe') || t.includes('gown') || t.includes('isolation') || t.includes('glove')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/bedside_nurse_patient.jpg',
      photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
      defaultComponentIds: ['candidate', 'ppe_station', 'resident', 'bed', 'waste_bin', 'sink', 'soap', 'towels']
    };
  }

  // 6. Bathing / Partial Bed Bath / Perineal Care / Hygiene
  if (t.includes('bath') || t.includes('perineal') || t.includes('peri care') || t.includes('back rub') || t.includes('foot care') || t.includes('mouth care') || t.includes('denture')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/bedside_nurse_patient.jpg',
      photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'bath_basin', 'overbed_table', 'privacy_curtain', 'sink', 'waste_bin']
    };
  }

  // 7. Bed Making & Clean Linens (Occupied or Unoccupied Bed)
  if ((t.includes('bed') && (t.includes('make') || t.includes('making') || t.includes('linen') || t.includes('unoccupied') || t.includes('occupied')))) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/bedside_nurse_patient.jpg',
      photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'clean_linens', 'overbed_table', 'privacy_curtain', 'waste_bin']
    };
  }

  // 8. Feeding / Dining Tray / Meal Assistance
  if (t.includes('feed') || t.includes('meal') || t.includes('dining') || t.includes('intake') || t.includes('diet')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/bedside_nurse_patient.jpg',
      photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'meal_tray', 'overbed_table', 'call_light', 'sink', 'clean_linens']
    };
  }

  // 9. Urinary Drainage Bag / Catheter Care
  if (t.includes('catheter') || t.includes('drainage') || t.includes('urinary') || t.includes('emptying')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/bedside_nurse_patient.jpg',
      photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'catheter_kit', 'overbed_table', 'privacy_curtain', 'ppe_station']
    };
  }

  // 10. Range of Motion / ROM Exercises
  if (t.includes('motion') || t.includes('rom') || t.includes('passive') || t.includes('flexion') || t.includes('abduction')) {
    return {
      environment: 'bedside_room',
      imageUrl: '/images/environments/bedside_nurse_patient.jpg',
      photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'privacy_curtain', 'call_light']
    };
  }

  // 11. Exam Room / Medical Assistant / Physical Assessment / Diagnostic
  if (t.includes('exam') || t.includes('clinic') || t.includes('assessment') || t.includes('palpat') || preferredEnv === 'exam_room') {
    return {
      environment: 'exam_room',
      imageUrl: '/images/environments/exam_room_nurse_patient.jpg',
      photosphereUrl: '/images/environments/exam_room_nurse_patient_360.jpg',
      defaultComponentIds: ['candidate', 'resident', 'exam_table', 'sink', 'call_light', 'vitals_cuff']
    };
  }

  // 12. ICU Bay / High Acuity / Telemetry / Critical Care
  if (t.includes('icu') || t.includes('intensive') || t.includes('cardiac monitor') || preferredEnv === 'icu_bay') {
    return {
      environment: 'icu_bay',
      imageUrl: '/images/environments/icu_bay.jpg',
      photosphereUrl: '/images/environments/clinical_icu_bay_1790100051302.jpg',
      defaultComponentIds: ['candidate', 'resident', 'bed', 'call_light', 'sink', 'vitals_cuff']
    };
  }

  // 7. General Bedside Resident Care (Bed Making, Catheter, Perineal, Feeding, Bath, ROM, Dressing)
  return {
    environment: 'bedside_room',
    imageUrl: '/images/environments/bedside_nurse_patient.jpg',
    photosphereUrl: '/images/environments/bedside_nurse_patient_all_fixtures_360.jpg',
    defaultComponentIds: ['candidate', 'resident', 'bed', 'overbed_table', 'call_light', 'privacy_curtain', 'sink']
  };
}

export function buildSmartClinicalPrompt(
  environment: string,
  scenarioTitle?: string,
  selectedComponentIds: string[] = [],
  isPhotosphere3D?: boolean,
  includeStudentActor: boolean = true
): string {
  const compSet = new Set(selectedComponentIds);
  const hasCandidate = compSet.has('candidate') || includeStudentActor;
  const hasResident = compSet.has('resident');
  const hasBed = compSet.has('bed');
  const hasWheelchair = compSet.has('wheelchair');
  const hasVitalsCuff = compSet.has('vitals_cuff');
  const hasOverbedTable = compSet.has('overbed_table');
  const hasCallLight = compSet.has('call_light');
  const hasPrivacyCurtain = compSet.has('privacy_curtain');
  const hasSink = compSet.has('sink');
  const hasFaucet = compSet.has('faucet');
  const hasSoap = compSet.has('soap');
  const hasTowels = compSet.has('towels');
  const hasWasteBin = compSet.has('waste_bin');
  const hasHygienePoster = compSet.has('hygiene_poster');
  const hasExamTable = compSet.has('exam_table');
  const hasPhlebChair = compSet.has('phlebotomy_chair');
  const hasSharpsBin = compSet.has('sharps_bin');
  const hasPpe = compSet.has('ppe_gear');
  const hasBathBasin = compSet.has('bath_basin');
  const hasLinens = compSet.has('clean_linens');
  const hasMealTray = compSet.has('meal_tray');
  const hasCatheterKit = compSet.has('catheter_kit');
  const hasFootBasin = compSet.has('foot_basin');
  const hasDentureCup = compSet.has('denture_cup');

  const titleLower = (scenarioTitle || '').toLowerCase();
  const isAmbulationSkill = titleLower.includes('ambulate') || titleLower.includes('gait') || titleLower.includes('transfer') || hasWheelchair;
  const isPulseVitalsSkill = titleLower.includes('pulse') || titleLower.includes('vital') || titleLower.includes('respirat') || titleLower.includes('blood pressure') || titleLower.includes('bp') || hasVitalsCuff;
  const isHandwashingSkill = environment === 'handwashing_station' || titleLower.includes('handwash') || titleLower.includes('hygiene') || titleLower.includes('asepsis');
  const isExamRoom = environment === 'exam_room' || titleLower.includes('exam') || titleLower.includes('clinic');
  const isPhlebotomy = environment === 'phlebotomy_chair' || titleLower.includes('phlebotomy') || titleLower.includes('venipuncture') || titleLower.includes('blood draw');
  const isIcu = environment === 'icu_bay' || titleLower.includes('icu') || titleLower.includes('intensive');
  const isFeeding = titleLower.includes('feed') || titleLower.includes('dining') || titleLower.includes('meal') || hasMealTray;
  const isBedMaking = titleLower.includes('bed making') || titleLower.includes('linen') || titleLower.includes('occupied bed') || hasLinens;
  const isBathingOrPerineal = titleLower.includes('bath') || titleLower.includes('perineal') || titleLower.includes('catheter') || hasBathBasin || hasCatheterKit;
  const isFootCare = titleLower.includes('foot') || titleLower.includes('nail') || hasFootBasin;
  const isOralCare = titleLower.includes('mouth') || titleLower.includes('denture') || titleLower.includes('oral') || hasDentureCup;
  const isRom = titleLower.includes('range of motion') || titleLower.includes('rom') || titleLower.includes('passive');

  // Human subjects description
  let actorsDesc = '';
  if (hasCandidate && hasResident) {
    if (isAmbulationSkill) {
      actorsDesc = 'A professional healthcare CNA candidate in clean royal blue scrubs is standing attentively beside an elderly resident patient wearing a snug canvas gait belt with buckle, assisting them safely with proper underhand grip and locked wheelchair nearby.';
    } else if (isPulseVitalsSkill) {
      actorsDesc = 'A healthcare nurse candidate in royal blue scrubs is seated beside an elderly resident patient resting comfortably in bed, gently supporting the resident\'s wrist and counting radial pulse with two fingers while observing a wristwatch second hand.';
    } else if (isPhlebotomy) {
      actorsDesc = 'A certified phlebotomist technician in royal blue scrubs and exam gloves is positioned beside the patient seated in an ergonomic phlebotomy armrest chair, with patient\'s arm supported and tourniquet applied for safe venipuncture.';
    } else if (isExamRoom) {
      actorsDesc = 'A professional healthcare nurse / medical assistant in royal blue scrubs is consulting attentively with an adult patient seated comfortably on the examination table.';
    } else if (isFeeding) {
      actorsDesc = 'A compassionate CNA candidate in royal blue scrubs is sitting at eye level with an elderly resident patient in an upright 90-degree Fowler\'s position, assisting with meal tray dining, offering sips of water and conversational encouragement.';
    } else if (isBedMaking) {
      actorsDesc = 'A skilled CNA candidate in royal blue scrubs is carefully rolling clean bottom linens and mitered hospital corners on the hospital bed beside the resting resident patient, maintaining safety rails.';
    } else if (isBathingOrPerineal) {
      actorsDesc = 'A CNA candidate in royal blue scrubs and exam gloves is providing gentle, dignified bedside hygiene care to an elderly resident patient covered with a warm privacy bath blanket.';
    } else if (isRom) {
      actorsDesc = 'A CNA candidate in royal blue scrubs is gently supporting an elderly resident\'s extremity at the joints while smoothly executing passive range of motion exercises within pain-free tolerance.';
    } else if (isFootCare) {
      actorsDesc = 'A CNA candidate in royal blue scrubs is seated on a low stool beside the resident, gently bathing the resident\'s foot in a warm water basin on a protective barrier pad.';
    } else if (isOralCare) {
      actorsDesc = 'A CNA candidate in royal blue scrubs is assisting a resident in upright Fowler\'s position with mouth care using a soft toothbrush, emesis basin, and clean towel barrier.';
    } else {
      actorsDesc = 'A professional healthcare student candidate in clean royal blue scrubs is attentively interacting with and providing patient-centered bedside care to an elderly resident patient resting in the clinical room.';
    }
  } else if (hasCandidate) {
    if (isHandwashingSkill) {
      actorsDesc = 'A healthcare candidate in royal blue scrubs is standing directly in front of the clinical stainless steel sink, actively lathering hands and wrists with rich soap foam under running warm water with fingertips angled downward.';
    } else if (isPhlebotomy) {
      actorsDesc = 'A phlebotomist candidate in royal blue scrubs and protective gloves is preparing the vacutainer tube collection tray and sterile supplies at the phlebotomy station.';
    } else {
      actorsDesc = 'A healthcare candidate in royal blue scrubs is actively demonstrating professional clinical procedure with medical supplies in the room.';
    }
  } else if (hasResident) {
    actorsDesc = 'An elderly resident patient is resting comfortably in the room in clean medical gown attire.';
  }

  // Active fixtures detailed listing
  const fixtureParts: string[] = [];
  if (hasBed) fixtureParts.push('adjustable electric hospital bed with side safety rails, head-of-bed controls, and clean linens');
  if (hasWheelchair) fixtureParts.push('transport wheelchair with locked wheel brakes, swung-away footrests, and canvas gait belt');
  if (hasVitalsCuff) fixtureParts.push('manual blood pressure cuff with calibrated dial manometer, dual-head stethoscope, and wristwatch');
  if (hasOverbedTable) fixtureParts.push('rolling adjustable height overbed table equipped with a clean wash basin and supplies barrier');
  if (hasCallLight) fixtureParts.push('emergency red nurse call light pendant cord within immediate reach of patient');
  if (hasPrivacyCurtain) fixtureParts.push('ceiling-track privacy curtain pulled to ensure complete resident dignity');
  if (hasSink) fixtureParts.push('clinical stainless steel hand hygiene sink basin with deep basin');
  if (hasFaucet) fixtureParts.push('chrome gooseneck faucet with warm water flowing');
  if (hasSoap) fixtureParts.push('wall-mounted antibacterial liquid soap dispenser');
  if (hasTowels) fixtureParts.push('wall-mounted commercial paper towel dispenser');
  if (hasWasteBin) fixtureParts.push('stainless steel foot-pedal waste bin labeled "USED TOWELS"');
  if (hasHygienePoster) fixtureParts.push('state board 6-step hand hygiene technique instructional wall chart');
  if (hasExamTable) fixtureParts.push('padded examination table with sanitary disposable paper roll');
  if (hasPhlebChair) fixtureParts.push('ergonomic phlebotomy armrest chair with multi-tube vacutainer collection tray');
  if (hasSharpsBin) fixtureParts.push('wall-mounted red biohazard sharps disposal container');
  if (hasPpe) fixtureParts.push('PPE supply dispenser with examination gloves, isolation gowns, and surgical masks');
  if (hasBathBasin) fixtureParts.push('wash basin with warm water, soap, washcloths, and bath towel stack');
  if (hasLinens) fixtureParts.push('stack of clean fitted sheets, flat sheets, draw sheet, and pillowcases');
  if (hasMealTray) fixtureParts.push('insulated meal dining tray with covered plate, soup bowl, cup with straw, and intake log sheet');
  if (hasCatheterKit) fixtureParts.push('antiseptic wipes, chux waterproof barrier pads, and graduated drainage collection cylinder');
  if (hasFootBasin) fixtureParts.push('foot washing basin, mild skin cleanser, moisturizing lotion, and non-skid socks');
  if (hasDentureCup) fixtureParts.push('labeled denture cup, denture brush, toothpaste, and curved kidney emesis basin');

  const fixturesClause = fixtureParts.length > 0
    ? `Prominently visible required clinical tools and fixtures include: ${fixtureParts.join('; ')}.`
    : '';

  if (isPhotosphere3D) {
    const envHeader = isHandwashingSkill
      ? 'A full 360-degree seamless equirectangular panoramic VR photograph (2:1 projection) of an authentic state board clinical hospital scrub room and hand hygiene station.'
      : isExamRoom
      ? 'A full 360-degree seamless equirectangular panoramic VR photograph (2:1 projection) of an authentic outpatient medical clinic examination room.'
      : isPhlebotomy
      ? 'A full 360-degree seamless equirectangular panoramic VR photograph (2:1 projection) of an authentic clinical phlebotomy blood draw station.'
      : isIcu
      ? 'A full 360-degree seamless equirectangular panoramic VR photograph (2:1 projection) of an intensive care unit (ICU) hospital room with cardiac monitors and infusion pumps.'
      : `A full 360-degree seamless equirectangular panoramic VR photograph (2:1 projection) of an authentic long-term care hospital bedside room tailored for ${scenarioTitle || 'Resident Clinical Care'}.`;

    return `${envHeader} ${actorsDesc} ${fixturesClause} Seamless 360 equirectangular VR photosphere projection, realistic hospital fluorescent and daylight illumination, full 360 environmental view with floor, ceiling, and all four walls visible in complete detail.`.replace(/\s+/g, ' ').trim();
  }

  // 16:9 Photography mode
  const photoHeader = isHandwashingSkill
    ? 'Photorealistic documentary-style clinical photograph (16:9 aspect ratio) of an authentic hospital hand hygiene station.'
    : isExamRoom
    ? 'Photorealistic wide-angle interior photograph (16:9 aspect ratio) of an authentic outpatient medical clinic examination room.'
    : isPhlebotomy
    ? 'Photorealistic clinical photograph (16:9 aspect ratio) of a dedicated clinical phlebotomy blood collection station.'
    : isIcu
    ? 'Photorealistic interior photograph (16:9 aspect ratio) of a high-acuity hospital ICU room with telemetry monitors.'
    : `Photorealistic wide-angle interior photograph (16:9 aspect ratio) of an authentic healthcare clinical skill room for ${scenarioTitle || 'Resident Bedside Care'}.`;

  return `${photoHeader} ${actorsDesc} ${fixturesClause} Professional clinical lighting, sharp foreground focus, true-to-life medical exam laboratory environment, 8k resolution realism.`.replace(/\s+/g, ' ').trim();
}

export const HANDWASHING_HOTSPOTS_PRESET = [
  {
    id: 'hs_faucet_water',
    name: 'Chrome Running Water Faucet',
    zone: 'bed',
    coordinates: { x: 44, y: 52 },
    sphericalCoordinates: { yaw: -10, pitch: -2 },
    requiredPhase: 'preparation' as const,
    availableActions: [
      {
        actionId: 'act_turn_on_water',
        label: 'Turn on water with clean paper towel & adjust to warm temperature',
        feedbackDialogue: 'Water is running warm. Candidate discards paper towel without touching sink.',
        isCritical: true,
        pointsAwarded: 15,
        category: 'technique' as const
      },
      {
        actionId: 'act_turn_off_water_barrier',
        label: 'Turn off faucet using a clean, dry paper towel barrier (do not touch with bare hands)',
        feedbackDialogue: 'Faucet turned off safely with clean paper towel barrier. No re-contamination.',
        isCritical: true,
        pointsAwarded: 20,
        category: 'safety' as const
      }
    ]
  },
  {
    id: 'hs_sink_basin',
    name: 'Stainless Steel Sink Basin',
    zone: 'sink',
    coordinates: { x: 44, y: 70 },
    sphericalCoordinates: { yaw: -10, pitch: -22 },
    requiredPhase: 'execution' as const,
    availableActions: [
      {
        actionId: 'act_wet_hands',
        label: 'Wet hands and wrists thoroughly, keeping hands pointed downwards below elbows',
        feedbackDialogue: 'Hands and wrists wetted completely. Water flows downwards without splashing uniform.',
        isCritical: true,
        pointsAwarded: 15,
        category: 'technique' as const
      },
      {
        actionId: 'act_rinse_downward',
        label: 'Rinse hands and wrists thoroughly with fingertips downward under running water',
        feedbackDialogue: 'Soap completely rinsed off with fingers pointing down into sink basin.',
        isCritical: true,
        pointsAwarded: 15,
        category: 'technique' as const
      }
    ]
  },
  {
    id: 'hs_soap_dispenser',
    name: 'Wall-Mounted Liquid Soap Dispenser',
    zone: 'supplies',
    coordinates: { x: 24, y: 28 },
    sphericalCoordinates: { yaw: -42, pitch: 24 },
    requiredPhase: 'execution' as const,
    availableActions: [
      {
        actionId: 'act_dispense_soap',
        label: 'Dispense adequate liquid soap onto wet hands',
        feedbackDialogue: 'Liquid antimicrobial soap dispensed onto palms.',
        isCritical: true,
        pointsAwarded: 10,
        category: 'hygiene' as const
      }
    ]
  },
  {
    id: 'hs_candidate_hands',
    name: 'Candidate Hands (Friction & Lather)',
    zone: 'resident',
    coordinates: { x: 62, y: 46 },
    sphericalCoordinates: { yaw: 22, pitch: 5 },
    requiredPhase: 'execution' as const,
    availableActions: [
      {
        actionId: 'act_scrub_20s',
        label: 'Lather and scrub all surfaces (palms, back of hands, interlacing fingers, nails) for full 20 seconds',
        feedbackDialogue: 'Candidate vigorously friction-cleans all hand surfaces for full 20 seconds.',
        isCritical: true,
        pointsAwarded: 20,
        category: 'technique' as const
      }
    ]
  },
  {
    id: 'hs_paper_towels',
    name: 'Paper Towel Dispenser',
    zone: 'call_light',
    coordinates: { x: 9, y: 44 },
    sphericalCoordinates: { yaw: -68, pitch: 8 },
    requiredPhase: 'exit' as const,
    availableActions: [
      {
        actionId: 'act_dry_clean_towels',
        label: 'Dry hands and wrists thoroughly with clean paper towels, patting from fingertips to wrists',
        feedbackDialogue: 'Hands and wrists patted completely dry starting at cleanest fingertips.',
        isCritical: true,
        pointsAwarded: 10,
        category: 'hygiene' as const
      }
    ]
  },
  {
    id: 'hs_waste_bin',
    name: 'Stainless "USED TOWELS" Waste Bin',
    zone: 'waste_hamper',
    coordinates: { x: 17, y: 84 },
    sphericalCoordinates: { yaw: -52, pitch: -38 },
    requiredPhase: 'exit' as const,
    availableActions: [
      {
        actionId: 'act_discard_used_towel',
        label: 'Discard used paper towels into waste receptacle without touching container rim',
        feedbackDialogue: 'Used towels safely disposed of without contamination.',
        isCritical: false,
        pointsAwarded: 10,
        category: 'safety' as const
      }
    ]
  },
  {
    id: 'hs_hygiene_poster',
    name: 'Hand Hygiene Steps Guide',
    zone: 'privacy_curtain',
    coordinates: { x: 44, y: 20 },
    sphericalCoordinates: { yaw: -10, pitch: 34 },
    requiredPhase: 'any' as const,
    availableActions: [
      {
        actionId: 'act_review_steps',
        label: 'Review CDC & WHO 6-step handrub / handwash protocol',
        feedbackDialogue: 'Candidate verifies adherence to state board protocol.',
        isCritical: false,
        pointsAwarded: 5,
        category: 'technique' as const
      }
    ]
  }
];
